import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { CardContent } from '@mui/material';
import { useConfig, useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import moment from 'moment/moment';
import { useEffect, useMemo, useState } from 'react';
import { usePatientProfile } from '../../../../../../hooks/use-patient-profile';
import { ContactApi } from '../../../../../../services/api-client/contact-api';
import { TreatmentPlanModel } from '../../../../../../services/api-client/contact-api.types';
import {
  DotSeparatedTitle,
  LiSeparator,
  List,
  ListItem
} from '../../../../../layout/slideout/patient-profile-widget/patient-profile-widget.styles';
import { AutoFollowUpStatusComponent } from '../../../../../layout/slideout/patient-profile-widget/views/treatment-plans/auto-followup-status/auto-followup-status';
import { TreatmentPlanItemsDialog } from '../../../../../layout/slideout/patient-profile-widget/views/treatment-plans/edit-items-dialog/treatment-plan-items-dialog';
import { TreatmentPlanStatusComponent } from '../../../../../layout/slideout/patient-profile-widget/views/treatment-plans/treatment-plan-status';
import {
  ItemContent,
  ItemHeader
} from '../../../../../layout/slideout/patient-profile-widget/views/treatment-plans/treatment-plan.styles';
import { TreatmentPlanSkeleton } from '../../../../../layout/slideout/patient-profile-widget/views/treatment-plans/treatment-plans.skeleton';
import { NoDataComponent } from '../../../../../shared/grid/no-data';
import { usePatientProfilePage } from '../../hooks';
import { PatientProfileRoutePath } from '../../navigation/route-config';
import { Card, CardTitle } from '../../patient-profile-page.styles';

export default function TreatmentPlans() {
  const { goTo } = usePatientProfilePage();
  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const { contactDetails, locationId } = usePatientProfile();
  const [treatmentPlans, setTreatmentPlans] = useState<TreatmentPlanModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<TreatmentPlanModel>();
  const [dialogOpened, setDialogOpened] = useState(false);
  const { showError } = useNotification();
  const contactApi = useMemo(() => ContactApi.initialize(config, oidcService), []);

  useEffect(() => {
    loadTreatmentPlans();
  }, []);

  const loadTreatmentPlans = async () => {
    setIsLoading(true);

    try {
      const treatmentPlans = await contactApi.getTreatmentPlans(contactDetails.id, locationId);
      const topThreeTreatmentPlans = treatmentPlans.slice(0, 3);
      setTreatmentPlans(topThreeTreatmentPlans);
    } catch {
      showError('Unable to load treatment plans.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleItemClick = (plan: TreatmentPlanModel) => {
    setSelectedItem(plan);
    setDialogOpened(true);
  };

  const handleNoteDialogClose = async () => {
    setDialogOpened(false);
    setSelectedItem(undefined);
  };

  const handleTreatmentPlanUpdate = (plan: TreatmentPlanModel) => {
    setTreatmentPlans((prev) => prev.map((tp) => (tp.id === plan.id ? plan : tp)));
    setSelectedItem(plan);
  };

  return (
    <>
      <Card sx={{ marginTop: '10px' }}>
        <CardContent>
          <CardTitle className="clickable" onClick={() => goTo(PatientProfileRoutePath.TREATMENT_PLANS)}>
            Treatment Plans
            <ArrowForwardIosIcon />
          </CardTitle>
          {isLoading && <TreatmentPlanSkeleton />}
          {!isLoading && treatmentPlans.length === 0 && <NoDataComponent />}
          {!isLoading && treatmentPlans.length > 0 && (
            <List>
              {treatmentPlans.map((tp, index) => {
                return (
                  <>
                    <ListItem key={tp.id} onClick={() => handleItemClick(tp)}>
                      <ItemHeader>
                        <DotSeparatedTitle
                          leftTitle={tp.dateCreated ? moment(tp.dateCreated).format('MM/DD/YYYY') : null}
                          rightTitle={<TreatmentPlanStatusComponent status={tp.status} sx={{ fontWeight: 400 }} />}
                        />
                        <AutoFollowUpStatusComponent autoFollowUpStatus={tp.autoFollowUpStatus} />
                      </ItemHeader>
                      <ItemContent>{tp.description}</ItemContent>
                    </ListItem>
                    {index !== treatmentPlans.length - 1 && <LiSeparator />}
                  </>
                );
              })}
            </List>
          )}
        </CardContent>
      </Card>
      {selectedItem && (
        <TreatmentPlanItemsDialog
          isOpen={dialogOpened}
          treatmentPlan={selectedItem}
          onTreatmentPlanUpdate={handleTreatmentPlanUpdate}
          onClose={handleNoteDialogClose}
        />
      )}
    </>
  );
}
